import * as API from "../api"
import { Project, MemberRole } from "../model/project-model"
import { User } from "../model/user-model"
import { isCloudApi } from "../api"

export const GENERAL_PROJECT_ID = 1
export const UNSELECTED_PROJECT_ID = -1

export const isCustomProject = (project: Project): boolean => project && project.id > GENERAL_PROJECT_ID
export const getProjectId = (project: Project) => project ? project.id :  UNSELECTED_PROJECT_ID
export const isSameProject = (p1: Project, p2: Project) => {
    if (!p1 && !p2) return true
    if (!p1 || !p2) return false
    return p1.id === p2.id
}

/**
 * Gets the current user's role in the project
 * Checks explicit membership first, then falls back to grantRoleToAll setting
 * @param project The project to check
 * @param user The current user
 * @returns The user's role (OWNER, CAN_EDIT, CAN_VIEW, or NONE), or null if user has no access
 */
export const getUserRoleInProject = (project: Project, user: User): MemberRole | null => {
    
    if (!project || !user) {
        return null
    }

    // First, check if user is explicitly in the members list
    if (project.members && project.members.length > 0) {
        const member = project.members.find(m => {
            if (isCloudApi()) {
                return m.login === user.email || (m.user && m.user.email === user.email)
            } else {
                return m.login === user.userId || 
                       m.login === user.username || 
                       (m.user && (m.user.userId === user.userId || m.user.username === user.username))
            }
        })

        if (member) {
            return member.role as MemberRole
        }
    }

    // If not explicitly a member, check grantRoleToAll setting
    if (project.grantRoleToAll && project.grantRoleToAll !== "NONE") {
        return project.grantRoleToAll as MemberRole
    }

    // No access
    return null
}

/**
 * Checks if the user can edit the project (OWNER or CAN_EDIT)
 */
export const canEditProject = (project: Project, user: User): boolean => {
    const role = getUserRoleInProject(project, user)
    return role === "OWNER" || role === "CAN_EDIT"
}

/**
 * Checks if the user is the owner of the project
 */
export const isProjectOwner = (project: Project, user: User): boolean => {
    const role = getUserRoleInProject(project, user)
    return role === "OWNER"
}

export const getProjects = (): Promise<Project[]> => {
        return API.getProjects().then(response => {
            if (response.ok) {
                return response.data
            } else {
                throw new Error("Error Get Projects!")
            }
        })
    }

export const getProject = (id: number): Promise<Project> =>
    API.getProject(id).then(response => {
        if (response.ok) {
            return response.data
        } else {
            throw new Error("Error Get Project!")
        }
    })


export const addProject = (project: Project): Promise<Project> =>
    API.addProject(project).then(response => {
        if (response.ok) {
            return response.data
        } else {
            throw new Error("Error Add Project!")
        }
    })

export const updateProject = (project: Project): Promise<Project> =>
    API.updateProject(project).then(response => {
        if (response.ok) {
            return response.data
        } else {
            throw new Error("Error Update Project!")
        }
    })


export const saveProject = (project: Project): Promise<Project> => {
    if ("id" in project && project.id !== null) {
        return updateProject(project)
    } else {
        return addProject(project)
    }
}

export const deleteProject = (id: number): Promise<Project> => {
    return API.deleteProject(id).then(response => {
        if (response.ok) {
            return response.data
        } else {
            throw new Error("Error Delete Project!")
        }
    })
}
